import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Pencil, Plus, Star } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { GestorDashboardFilterPreset } from '@/components/Gestores/gestorDashboardPresetTypes'

type Props = {
  presets: GestorDashboardFilterPreset[]
  activePresetId: string | null
  presetsLoading?: boolean
  onSelect: (preset: GestorDashboardFilterPreset) => void
  onCreate: () => void
  onEdit: (preset: GestorDashboardFilterPreset) => void
}

const MENU_WIDTH = 352

function useAnchoredMenuStyle(open: boolean, anchorRef: RefObject<HTMLElement | null>) {
  const [style, setStyle] = useState<CSSProperties>({})

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return

    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect()
      const maxHeight = 288
      const gap = 8
      const isMobile = window.innerWidth < 768

      if (isMobile) {
        setStyle({
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: 16,
          width: 'auto',
          maxHeight: 'min(70vh, 24rem)',
          zIndex: 100,
        })
        return
      }

      const width = Math.min(MENU_WIDTH, window.innerWidth - 32)
      const left = Math.min(Math.max(16, rect.left), window.innerWidth - width - 16)
      const spaceBelow = window.innerHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap
      const openUp = spaceBelow < 200 && spaceAbove > spaceBelow
      const height = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow)

      setStyle({
        position: 'fixed',
        left,
        width,
        maxHeight: height,
        zIndex: 100,
        ...(openUp ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef])

  return style
}

export function GestorDashboardSavedFilters({
  presets,
  activePresetId,
  presetsLoading = false,
  onSelect,
  onCreate,
  onEdit,
}: Props) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuStyle = useAnchoredMenuStyle(open, buttonRef)
  const activePreset = presets.find((preset) => preset.id === activePresetId)

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  const menu =
    open &&
    createPortal(
      <>
        <div
          className='fixed inset-0 z-[90] bg-black/40 md:hidden'
          aria-hidden
          onClick={() => setOpen(false)}
        />
        <div
          ref={menuRef}
          role='menu'
          style={menuStyle}
          className='overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl'
        >
          <div className='border-b border-[var(--glass-border)] px-3 py-2 text-xs font-semibold italic text-text-muted'>
            Meus filtros
          </div>

          <div className='max-h-72 overflow-y-auto'>
            {presetsLoading ? (
              <p className='px-3 py-4 text-sm text-text-muted'>Carregando filtros…</p>
            ) : presets.length === 0 ? (
              <p className='px-3 py-4 text-sm text-text-muted'>Nenhum filtro salvo ainda.</p>
            ) : (
              presets.map((preset) => {
                const active = preset.id === activePresetId
                return (
                  <div
                    key={preset.id}
                    className={cn(
                      'flex items-center justify-between gap-2 border-b border-[var(--glass-border)] px-2 py-1 last:border-b-0',
                      active && 'bg-accent/10',
                    )}
                  >
                    <button
                      type='button'
                      role='menuitem'
                      onClick={() => {
                        onSelect(preset)
                        setOpen(false)
                      }}
                      className={cn(
                        'flex min-w-0 flex-1 items-center justify-between gap-3 px-1 py-1.5 text-left text-sm transition-colors',
                        active ? 'text-accent' : 'text-text-primary hover:text-text-primary',
                      )}
                    >
                      <span className='min-w-0 truncate'>{preset.name}</span>
                      <span className='flex shrink-0 items-center gap-2 text-text-muted'>
                        {preset.isDefault && <Star size={12} className='text-accent' aria-hidden />}
                        <span>{preset.matchCount}</span>
                        {active && <Check size={14} aria-hidden />}
                      </span>
                    </button>
                    <button
                      type='button'
                      aria-label={`Editar filtro ${preset.name}`}
                      onClick={() => {
                        onEdit(preset)
                        setOpen(false)
                      }}
                      className='rounded p-2 text-text-muted hover:bg-white/[0.08] hover:text-text-primary'
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                )
              })
            )}
          </div>

          <button
            type='button'
            onClick={() => {
              onCreate()
              setOpen(false)
            }}
            className='flex w-full items-center gap-2 border-t border-[var(--glass-border)] px-3 py-3 text-sm font-medium text-accent hover:bg-white/[0.04]'
          >
            <Plus size={15} aria-hidden />
            Salvar filtro atual
          </button>
        </div>
      </>,
      document.body,
    )

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(
          'flex h-10 min-w-[10rem] max-w-[16rem] items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors',
          open || activePreset
            ? 'border-accent/30 bg-accent/10 text-accent'
            : 'border-[var(--glass-border)] bg-[var(--glass-bg)] text-text-secondary hover:text-text-primary',
        )}
      >
        <Star size={15} className='shrink-0' aria-hidden />
        <span className='min-w-0 truncate'>{activePreset?.name ?? 'Meus filtros'}</span>
        <ChevronDown size={14} className={cn('ml-auto shrink-0 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {menu}
    </>
  )
}
